import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const compliancePoints = [
  'You are a large industrial entity covered under CCTS (cement, steel, aluminium, textiles, etc.)',
  'MoEFCC has notified your plant with a specific GHG intensity reduction target',
  'You are legally required to measure, report, and verify your annual emissions',
  'At cycle end, you must surrender enough CCCs to cover any shortfall or pay 2× compensation',
  'If you beat your target, you earn CCCs which you can sell on CERC-regulated exchanges',
];

const offsetPoints = [
  'You are a project developer, NGO, SME, or any entity not covered under CCTS mandatory targets',
  'You have a project that reduces, removes, or avoids GHG emissions (renewable energy, forestry, waste, etc.)',
  'You register the project under an approved methodology with BEE',
  'After verification by an ACVA, you can receive CCCs for the emission reductions achieved',
  'These CCCs can be sold to obligated entities needing compliance credits, or to voluntary buyers',
];

const scenarios = [
  {
    q: 'I run a 500MW solar farm',
    answer: 'Offset mechanism',
    detail: 'You are not an obligated entity. You can register your project under BEE\'s renewable energy methodology and earn Voluntary CCCs for displacing fossil-fuel generation. These can be sold to buyers needing offsets.',
    color: 'var(--neon-green)',
  },
  {
    q: 'I manage a cement plant with 10 lakh tonnes output per year',
    answer: 'Compliance mechanism',
    detail: 'You are almost certainly a covered entity under CCTS. You have notified intensity targets and must participate in the compliance market — measuring, verifying, and surrendering CCCs each cycle.',
    color: 'var(--neon-orange)',
  },
  {
    q: 'I am an SME in the textile sector',
    answer: 'Depends on your scale',
    detail: 'Small textile units below the notified threshold may not be obligated. Above the threshold you are a compliance entity. Below it, you can still participate via the voluntary offset mechanism if you have qualifying emission-reduction activities.',
    color: 'var(--neon-yellow)',
  },
  {
    q: 'I lead an NGO running agroforestry projects',
    answer: 'Offset mechanism',
    detail: 'Agriculture and forestry projects are eligible under BEE\'s offset methodologies. Your NGO can register, get verified, and earn CCCs for sequestration or avoided emissions — then sell them in the voluntary or compliance markets.',
    color: 'var(--neon-cyan)',
  },
  {
    q: 'I am a corporate sustainability manager at a non-obligated company',
    answer: 'Voluntary buyer',
    detail: 'You are not legally required to participate, but you can voluntarily purchase CCCs to offset your company\'s Scope 1/2/3 footprint, meet internal sustainability targets, or support ESG reporting.',
    color: 'var(--neon-pink)',
  },
  {
    q: 'I am an investor tracking carbon market opportunities',
    answer: 'Market participant (non-regulated)',
    detail: 'As an individual or institutional investor, you can follow the market, model scenarios on simulators like CarbonX, and explore project listings. Actual regulated trading on exchanges requires entity-level registration.',
    color: 'var(--neon-blue)',
  },
];

export default function ComplianceVsOffset() {
  const [activeScenario, setActiveScenario] = useState(null);

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag" style={{ color: 'var(--neon-green)' }}>Learn</div>
          <h1 className="section-title">Compliance vs Offset — Which one applies to you?</h1>
          <p className="section-copy">
            India's carbon market has two distinct pathways. The compliance mechanism covers obligated industrial entities. The offset mechanism is open to any organisation with qualifying emission-reduction projects.
          </p>
        </div>
      </section>

      {/* Side-by-side comparison */}
      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.8rem', borderTop: '3px solid var(--neon-orange)' }}>
          <div className="section-tag" style={{ color: 'var(--neon-orange)', marginBottom: '0.8rem' }}>Compliance Mechanism</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>For obligated entities</div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {compliancePoints.map((point, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(255,102,0,0.15)', border: '1px solid rgba(255,102,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--neon-orange)' }} />
                </div>
                <div style={{ color: 'var(--muted)', fontSize: '0.86rem', lineHeight: '1.7' }}>{point}</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginTop: '1.5rem', background: 'rgba(255,102,0,0.05)', borderColor: 'rgba(255,102,0,0.2)' }}>
            <div className="stat-label" style={{ color: 'var(--neon-orange)', marginBottom: '0.3rem' }}>Who this covers</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>Cement, Aluminium, Pulp & Paper, Chlor-Alkali, Iron & Steel, Petroleum Refinery, Petrochemicals, Fertilisers, Textiles — at notified plant-level thresholds.</div>
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.8rem', borderTop: '3px solid var(--neon-green)' }}>
          <div className="section-tag" style={{ color: 'var(--neon-green)', marginBottom: '0.8rem' }}>Offset Mechanism</div>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>For project developers & voluntary participants</div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {offsetPoints.map((point, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--neon-green)' }} />
                </div>
                <div style={{ color: 'var(--muted)', fontSize: '0.86rem', lineHeight: '1.7' }}>{point}</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginTop: '1.5rem', background: 'rgba(0,255,136,0.04)', borderColor: 'rgba(0,255,136,0.18)' }}>
            <div className="stat-label" style={{ color: 'var(--neon-green)', marginBottom: '0.3rem' }}>Who this covers</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>Renewable energy, agroforestry, waste management, CCUS, sustainable agriculture, and other approved project categories — for any entity not obligated under CCTS.</div>
          </div>
        </div>
      </section>

      {/* Decision tree */}
      <section className="section-stack">
        <div className="section-tag">Decision Tool</div>
        <h2 className="headline-sm" style={{ fontSize: '1.35rem' }}>Which pathway applies to your situation?</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: '640px' }}>Click a scenario to see the relevant pathway and what it means for you.</p>

        <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
          {scenarios.map((scenario, i) => (
            <div key={i}>
              <button
                type="button"
                onClick={() => setActiveScenario(activeScenario === i ? null : i)}
                className="surface-panel"
                style={{ width: '100%', padding: '1.1rem 1.4rem', textAlign: 'left', cursor: 'pointer', border: `1px solid ${activeScenario === i ? scenario.color : 'var(--border2)'}`, background: activeScenario === i ? `rgba(0,0,0,0.2)` : undefined, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}
              >
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.9rem', color: 'var(--text)' }}>{scenario.q}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
                  <span className="badge" style={{ color: scenario.color, borderColor: 'transparent', background: 'transparent', whiteSpace: 'nowrap' }}>{scenario.answer}</span>
                  <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{activeScenario === i ? '▲' : '▼'}</span>
                </div>
              </button>
              {activeScenario === i && (
                <div style={{ padding: '1rem 1.4rem', background: 'rgba(255,255,255,0.02)', border: `1px solid ${scenario.color}`, borderTop: 'none', color: 'var(--muted)', fontSize: '0.86rem', lineHeight: '1.75' }}>
                  {scenario.detail}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/learn/how-cccs-work" className="btn btn-secondary">How CCCs Work →</Link>
        <Link to="/learn/who-can-participate" className="btn btn-secondary">Who Can Participate →</Link>
        <Link to="/market/india" className="btn btn-secondary">India Market Overview</Link>
      </section>
    </div>
  );
}
